import { imageService } from '../lib/imageService.js'

/**
 * Test utilities for the image generation service
 * Available in browser console as window.testImageService
 */

const testImageService = {
  /**
   * Test basic image generation
   */
  async testGeneration() {
    console.log('🎨 Testing image generation...')
    
    try {
      const result = await imageService.generateImage({
        prompt: 'A cinematic movie poster for a sci-fi thriller',
        type: 'cover',
        userId: 'test-user-id',
        projectTitle: 'Test Project',
        genre: 'Sci-Fi',
        onProgress: (message) => console.log('Progress:', message)
      })
      
      console.log('✅ Image generation test successful:', result)
      return result
    } catch (error) {
      console.error('❌ Image generation test failed:', error)
      throw error
    }
  },

  /**
   * Test cover image generation
   */
  async testCoverGeneration() {
    console.log('🎬 Testing cover image generation...')
    
    try {
      const result = await imageService.generateCoverImage({
        projectTitle: 'The Last Stand',
        genre: 'Action',
        synopsis: 'A retired marine must defend his small town against alien invaders in this action-packed thriller.',
        userId: 'test-user-id',
        projectId: 'test-project-id',
        onProgress: (message) => console.log('Progress:', message)
      })
      
      console.log('✅ Cover image generation test successful:', result)
      return result
    } catch (error) {
      console.error('❌ Cover image generation test failed:', error)
      throw error
    }
  },

  /**
   * Test different image types
   */
  async testImageTypes() {
    console.log('🎭 Testing different image types...')
    
    const tests = [
      {
        type: 'character',
        prompt: 'A battle-hardened space marine in futuristic armor'
      },
      {
        type: 'scene', 
        prompt: 'A dramatic spaceship battle scene with explosions and laser fire'
      },
      {
        type: 'custom',
        prompt: 'A mysterious alien artifact glowing in a dark cave'
      }
    ]

    const results = []
    
    for (const test of tests) {
      try {
        console.log(`Testing ${test.type} image...`)
        const result = await imageService.generateImage({
          prompt: test.prompt,
          type: test.type,
          userId: 'test-user-id',
          onProgress: (message) => console.log(`${test.type}:`, message)
        })
        
        results.push({ type: test.type, success: true, result })
        console.log(`✅ ${test.type} image generated successfully`)
      } catch (error) {
        results.push({ type: test.type, success: false, error: error.message })
        console.error(`❌ ${test.type} image generation failed:`, error)
      }
    }
    
    console.log('🎭 Image type tests completed:', results)
    return results
  },

  /**
   * Test database operations
   */
  async testDatabase() {
    console.log('💾 Testing database operations...')
    
    try {
      // Test saving image data
      const mockImageData = {
        userId: 'test-user-id',
        projectId: 'test-project-id',
        url: 'https://example.com/test-image.jpg',
        width: 1024,
        height: 768,
        prompt: 'Test image prompt',
        enhancedPrompt: 'Enhanced test image prompt with professional styling',
        type: 'cover',
        projectTitle: 'Test Project',
        genre: 'Test Genre'
      }
      
      const saved = await imageService.saveGeneratedImage(mockImageData)
      console.log('✅ Image saved to database:', saved)
      
      // Test fetching user images
      const userImages = await imageService.getUserImages('test-user-id')
      console.log('✅ User images fetched:', userImages)
      
      return { saved, userImages }
    } catch (error) {
      console.error('❌ Database test failed:', error)
      throw error
    }
  },

  /**
   * Run all tests
   */
  async runAll() {
    console.log('🚀 Running all image service tests...')
    
    const results = {
      generation: null,
      coverGeneration: null,
      imageTypes: null,
      database: null
    }
    
    try {
      console.log('\n--- Basic Generation Test ---')
      results.generation = await this.testGeneration()
    } catch (error) {
      results.generation = { error: error.message }
    }
    
    try {
      console.log('\n--- Cover Generation Test ---')
      results.coverGeneration = await this.testCoverGeneration()
    } catch (error) {
      results.coverGeneration = { error: error.message }
    }
    
    try {
      console.log('\n--- Image Types Test ---')
      results.imageTypes = await this.testImageTypes()
    } catch (error) {
      results.imageTypes = { error: error.message }
    }
    
    try {
      console.log('\n--- Database Test ---')
      results.database = await this.testDatabase()
    } catch (error) {
      results.database = { error: error.message }
    }
    
    console.log('\n🎯 All tests completed:', results)
    return results
  }
}

// Make available in browser console for testing
if (typeof window !== 'undefined') {
  window.testImageService = testImageService
  console.log('🎨 Image Service tests available in browser console:')
  console.log('- window.testImageService.testGeneration()')
  console.log('- window.testImageService.testCoverGeneration()')
  console.log('- window.testImageService.testImageTypes()')
  console.log('- window.testImageService.testDatabase()')
  console.log('- window.testImageService.runAll()')
}

export default testImageService
